<template>
    <div class="email-template-editor">
        <div class="toolbar" v-if="!previewMode">
            <div class="left-buttons">
                <SupsisButton @click="$emit('close')" primary small> &lt; {{ $t("message.goBack") }}</SupsisButton>
                <SupsisButton @click="handleUpdateTemplate" primary small>
                    <i class="fas fa-save"></i> {{ $t("message.update") }}
                </SupsisButton>
            </div>
            <div class="right-buttons">
                <SupsisButton @click="handleMjmlModalOpen">MJML</SupsisButton>
                <SupsisButton @click="handleHtmlModalOpen">HTML {{ $t("message.show") }}</SupsisButton>
                <SupsisButton @click="handleModelModalOpen">Model {{ $t("message.show") }}</SupsisButton>
                <TemplateSelector @template-select="handleTemplateSelect" />
            </div>
        </div>

        <div ref="editor" class="grape-wrapper"></div>

        <template v-if="!previewMode">
            <Modal2 v-if="mjmlModalOpen" title="MJML" @cancel="mjmlModalOpen = false">
                {{ editor && stripMailPlaceholderSpans(editor.getHtml()) }}
            </Modal2>

            <Modal2 v-if="htmlModalOpen" title="HTML" @cancel="htmlModalOpen = false">
                {{ getRawHtml() }}
            </Modal2>

            <Modal2 v-if="modelModalOpen" title="Model" @cancel="modelModalOpen = false">
                {{ editor && JSON.stringify(editor.getComponents()) }}
            </Modal2>

            <a-modal
                v-model="mailPlaceholderModalOpen"
                title="Şablon özelliği"
                :footer="null"
                :width="380"
                @cancel="cancelMailPlaceholderModal"
            >
                <a-input
                    ref="mailPlaceholderInput"
                    v-model="mailPlaceholderInput"
                    placeholder="Alan adı"
                    @pressEnter="confirmMailPlaceholderInsert"
                />
                <div class="mail-placeholder-modal-actions">
                    <a-button @click="cancelMailPlaceholderModal">İptal</a-button>
                    <a-button type="primary" @click="confirmMailPlaceholderInsert">Ekle</a-button>
                </div>
            </a-modal>
        </template>
    </div>
</template>

<script>
import Vue from "vue";
import grapesjs from "grapesjs";
import gjsPresetNewsletter from "grapesjs-preset-newsletter";
import "grapesjs/dist/css/grapes.min.css";
import gjsMjml from "grapesjs-mjml";
import mjml2html from "mjml-browser";
import TemplateSelector from "./TemplateSelector.vue";
import Modal2 from "../../../../components/common/Modal2.vue";
import SupsisButton from "../../Settings/components/SupsisButton.vue";
import { MAIL_API } from "../../../../api/mail";
import { uploadAsset } from "../../../../api/assetUpload";
import axios from "axios";
import { mapState } from "vuex";

export default Vue.extend({
    name: "EmailTemplateEditor",
    components: {
        TemplateSelector,
        Modal2,
        SupsisButton,
    },
    props: {
        template: {
            type: Object,
            required: true,
        },
        previewMode: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            editor: null,
            mjmlModalOpen: false,
            htmlModalOpen: false,
            modelModalOpen: false,
            mailPlaceholderModalOpen: false,
            mailPlaceholderInput: "",
            pendingMailPlaceholderRte: null,
            savedMailPlaceholderRange: null,
            pendingCaretMarkerId: null,
            lastMailRteCaretRange: null,
        };
    },
    computed: {
        ...mapState({
            site: (state) => state.site.site,
        }),
    },
    mounted() {
        let design = this.template.design;
        if (typeof design === "string") {
            design = JSON.parse(design);
        }

        const config = {
            container: this.$refs.editor,
            plugins: [gjsPresetNewsletter, gjsMjml],
            pluginsOpts: {
                gjsPresetNewsletter: {},
                gjsMjml: {},
            },
            components: design,
            style: `
                .mj-text {
                    padding: 20px;
                }
                .gjs-mail-placeholder {
                    display: inline-flex;
                    align-items: center;
                    vertical-align: baseline;
                    gap: 4px;
                    max-width: 100%;
                    padding: 1px 6px 1px 4px;
                    margin: 0 1px;
                    border-radius: 3px;
                    background: #fafafa;
                    border: 1px solid #e8e8e8;
                    color: #424242;
                    font-family: inherit;
                    font-size: 12px;
                    font-weight: 500;
                    line-height: 1.45;
                    box-decoration-break: clone;
                    -webkit-box-decoration-break: clone;
                }
                .gjs-mail-placeholder::before {
                    content: "@";
                    flex-shrink: 0;
                    font-weight: 700;
                    font-size: 12px;
                    line-height: 1;
                    color: #757575;
                    user-select: none;
                    -webkit-user-select: none;
                    pointer-events: none;
                }
                .gjs-mail-placeholder-path {
                    display: inline;
                    min-width: 0.5em;
                    user-select: none;
                    cursor: default;
                }
                .gjs-mail-caret-marker {
                    display: inline-block;
                    width: 0;
                    max-width: 0;
                    overflow: hidden;
                    line-height: 0;
                    font-size: 0;
                    opacity: 0;
                    vertical-align: baseline;
                }
            `,
            storageManager: false,
            assetManager: {
                upload: false,
                uploadFile: false,
                multiUpload: false,
                autoAdd: false,
            },
        }
        if (this.previewMode) {
            config.canvas = {};
            config.commands = {};
            config.panels = {};
            config.deviceManager = {};
            config.selectorManager = {};
            config.editable = false;
            config.noticeOnUnload = false;
            config.showOffsets = false;
            config.showRulers = false;
            config.allowScripts = false;
            config.canvas.styles = [];
            config.canvas.scripts = [];
            config.commands.defaults = [];
            config.panels.defaults = [];
            config.deviceManager.devices = [];
            config.selectorManager.componentFirst = false;
        }
        const editor = grapesjs.init(config);

        this.editor = editor;

        // Asset upload için custom handler
        if (!this.previewMode) {
            this.setupAssetUpload(editor);
            this.setupMailPlaceholders(editor);
        }

        if (this.previewMode) {
            editor.getModel().set('draftMode', false);
            editor.Commands.stop('preview');
            editor.getModel().set('hoverable', false);
            editor.getModel().set('selectable', false);
            editor.getModel().set('draggable', false);
            editor.getModel().set('droppable', false);
            editor.getModel().set('highlightable', false);
            editor.refresh();

            editor.UndoManager.clear();
            editor.Panels.getPanels().reset();
            editor.getContainer().classList.add('gjs-preview-mode');
        }

        window.editor = editor;
    },
    beforeDestroy() {
        if (this.editor) {
            this.editor.destroy();
        }
    },
    methods: {
        /**
         * Asset Manager için S3 upload yapılandırması
         * @param {Object} editor - GrapesJS editor instance
         */
        setupAssetUpload(editor) {
            const vm = this;

            // Asset Manager'ın açılmasını izle - birden fazla event dinle
            const setupUI = () => {
                setTimeout(() => {
                    const am = editor.AssetManager;
                    const container = am.getContainer();

                    if (!container) return;

                    // Default upload alanını gizle
                    const defaultUploader = container.querySelector('.gjs-am-file-uploader');
                    if (defaultUploader) {
                        defaultUploader.style.display = 'none';
                    }

                    // Custom upload UI'ı ekle (eğer yoksa)
                    if (!container.querySelector('.custom-asset-upload')) {
                        vm.addCustomUploadUI(container);
                    }
                }, 150);
            };

            // Birden fazla event dinle
            editor.on('run:open-assets', setupUI);
            editor.on('asset:open', setupUI);

            // Modal açılma eventini de dinle
            editor.on('modal:open', setupUI);
        },

        /**
         * Custom upload UI'ı ekle
         * @param {HTMLElement} container - Asset Manager container
         */
        addCustomUploadUI(container) {
            const uniqueId = `gjs-asset-file-input-${Date.now()}`;
            const uploadDiv = document.createElement('div');
            uploadDiv.className = 'custom-asset-upload';
            uploadDiv.innerHTML = `
                <div class="asset-upload-area">
                    <input type="file" id="${uniqueId}" accept="image/*" multiple style="display: none;" />
                    <label for="${uniqueId}" class="asset-upload-label">
                        <i class="fa fa-cloud-upload"></i>
                        <p>Upload Asset</p>
                        <span>Click or drag images here</span>
                    </label>
                </div>
            `;

            // Container'a direkt prepend et (en üste ekle)
            if (container.firstChild) {
                container.insertBefore(uploadDiv, container.firstChild);
            } else {
                container.appendChild(uploadDiv);
            }

            // File input event listener
            const fileInput = uploadDiv.querySelector('input[type="file"]');

            fileInput.addEventListener('change', async (e) => {
                const files = e.target.files;
                if (files && files.length > 0) {
                    await this.uploadFiles(files);
                    e.target.value = ''; // Reset input
                }
            });

            // Drag & drop desteği
            const uploadArea = uploadDiv.querySelector('.asset-upload-area');

            uploadArea.addEventListener('dragover', (e) => {
                e.preventDefault();
                e.stopPropagation();
                uploadArea.classList.add('dragover');
            });

            uploadArea.addEventListener('dragleave', (e) => {
                e.preventDefault();
                e.stopPropagation();
                uploadArea.classList.remove('dragover');
            });

            uploadArea.addEventListener('drop', async (e) => {
                e.preventDefault();
                e.stopPropagation();
                uploadArea.classList.remove('dragover');

                const files = e.dataTransfer.files;
                if (files && files.length > 0) {
                    await this.uploadFiles(files);
                }
            });
        },

        /**
         * Dosyaları S3'e yükle
         * @param {FileList} files - Yüklenecek dosyalar
         */
        async uploadFiles(files) {
            const am = this.editor.AssetManager;

            if (!this.site || !this.site._id) {
                this.$message.error('Site information not found');
                return;
            }

            let uploadedCount = 0;

            try {
                this.$message.loading('Uploading assets...', 0);

                // Her dosyayı ayrı ayrı yükle
                for (let i = 0; i < files.length; i++) {
                    const file = files[i];

                    // File type kontrolü
                    if (!file.type.startsWith('image/')) {
                        this.$message.warning(`${file.name} is not an image file`);
                        continue;
                    }

                    const path = `${this.site._id}/email-assets/`;

                    try {
                        // 1. Upload metadata gönder, signed URL al
                        const uploadData = await uploadAsset({
                            path,
                            name: file.name,
                            size: file.size,
                            type: 'mail',
                            mime: file.type,
                            ext: file.name.split('.').pop(),
                        });

                        if (!uploadData || !uploadData.data || !uploadData.data.asset) {
                            throw new Error('Invalid upload response');
                        }

                        // 2. Dosyayı S3'e yükle
                        await axios({
                            method: uploadData.data.method,
                            url: uploadData.data.url,
                            data: file,
                            headers: { 'Content-Type': 'application/octet-stream' },
                        });

                        // 3. Final URL'i oluştur
                        const asset = uploadData.data.asset;
                        const assetUrl = asset.v2Format && asset.cdnUrl
                            ? asset.cdnUrl
                            : `https://static.supsis.live/${path}${asset._id}`;

                        uploadedCount++;

                        // 4. Asset Manager'a ekle
                        am.add({
                            src: assetUrl,
                            type: 'image',
                            name: file.name,
                        });
                    } catch (error) {
                        console.error(`Failed to upload ${file.name}:`, error);
                        this.$message.error(`Failed to upload ${file.name}`);
                    }
                }

                this.$message.destroy();

                if (uploadedCount > 0) {
                    this.$message.success(`${uploadedCount} asset(s) uploaded successfully`);
                } else if (files.length > 0) {
                    this.$message.warning('No assets were uploaded');
                }
            } catch (error) {
                this.$message.destroy();
                console.error('Asset upload error:', error);
                this.$message.error('Failed to upload assets');
            }
        },

        handleMjmlModalOpen() {
            this.mjmlModalOpen = true;
        },
        handleHtmlModalOpen() {
            this.htmlModalOpen = true;
        },
        handleModelModalOpen() {
            this.modelModalOpen = true;
        },
        handleTemplateSelect(template) {
            this.editor.setComponents(template);
        },
        normalizePlaceholderPathFromSpanInner(inner) {
            if (inner == null) {
                return "";
            }
            let t = String(inner).trim();
            const legacy = t.match(/^\{\{([\s\S]+)\}\}$/);
            if (legacy) {
                return legacy[1].trim();
            }
            t = t.replace(/<[^>]+>/g, "").trim();
            return t;
        },
        escapeHtmlAttr(str) {
            return String(str)
                .replace(/&/g, "&amp;")
                .replace(/"/g, "&quot;")
                .replace(/</g, "&lt;")
                .replace(/>/g, "&gt;");
        },
        escapeHtmlText(str) {
            return String(str)
                .replace(/&/g, "&amp;")
                .replace(/</g, "&lt;")
                .replace(/>/g, "&gt;");
        },
        unescapeHtmlAttr(str) {
            return String(str)
                .replace(/&quot;/g, '"')
                .replace(/&lt;/g, "<")
                .replace(/&gt;/g, ">")
                .replace(/&amp;/g, "&");
        },
        buildPlaceholderSpanHtml(path) {
            const p = (this.normalizePlaceholderPathFromSpanInner(path) || String(path || "")).trim();
            const safeAttr = this.escapeHtmlAttr(p);
            const safeText = this.escapeHtmlText(p);
            return (
                '<span class="gjs-mail-placeholder" data-mail-field="' +
                safeAttr +
                '" contenteditable="false" title="Düzenlemek için silip yeniden ekleyin">' +
                '<span class="gjs-mail-placeholder-path">' +
                safeText +
                "</span></span>"
            );
        },
        findBalancedMailPlaceholderSpanEnd(html, spanOpenIndex) {
            if (spanOpenIndex < 0 || html.substr(spanOpenIndex, 5) !== "<span") {
                return -1;
            }
            let depth = 0;
            let i = spanOpenIndex;
            while (i < html.length) {
                if (html.substr(i, 5) === "<span") {
                    depth++;
                    const gt = html.indexOf(">", i);
                    if (gt < 0) {
                        return -1;
                    }
                    i = gt + 1;
                } else if (html.substr(i, 7) === "</span>") {
                    depth--;
                    i += 7;
                    if (depth === 0) {
                        return i;
                    }
                } else {
                    i++;
                }
            }
            return -1;
        },
        extractPlaceholderPathFromBlock(block) {
            const pathInner = block.match(/class="[^"]*gjs-mail-placeholder-path[^"]*"[^>]*>([\s\S]*?)<\/span>/i);
            if (pathInner) {
                return this.normalizePlaceholderPathFromSpanInner(pathInner[1]);
            }
            const dm = block.match(/data-mail-field="([^"]*)"/);
            if (dm) {
                return this.unescapeHtmlAttr(dm[1]);
            }
            return "";
        },
        stripMailPlaceholderSpans(html) {
            if (!html || html.indexOf("gjs-mail-placeholder") < 0) {
                return html;
            }
            const self = this;
            let out = "";
            let i = 0;
            while (i < html.length) {
                const idx = html.indexOf("gjs-mail-placeholder", i);
                if (idx < 0) {
                    out += html.slice(i);
                    break;
                }
                const start = html.lastIndexOf("<span", idx);
                if (start < i || start < 0) {
                    out += html.slice(i, idx + 1);
                    i = idx + 1;
                    continue;
                }
                out += html.slice(i, start);
                const end = self.findBalancedMailPlaceholderSpanEnd(html, start);
                if (end < 0) {
                    out += html.slice(start);
                    break;
                }
                const block = html.slice(start, end);
                const path = self.extractPlaceholderPathFromBlock(block);
                out += "{{" + path + "}}";
                i = end;
            }
            return out;
        },
        wrapMailPlaceholdersInHtml(html) {
            if (!html) {
                return html;
            }
            const self = this;
            let step = html;
            if (step.indexOf("gjs-mail-placeholder") >= 0) {
                step = this.stripMailPlaceholderSpans(step);
            }
            if (step.indexOf("{{") < 0) {
                return html;
            }
            return step.replace(/\{\{([^}]+)\}\}/g, function (match, inner) {
                const path = String(inner).trim();
                return self.buildPlaceholderSpanHtml(path) + "\u200B";
            });
        },
        wrapAllMailPlaceholders(editor) {
            const self = this;
            const walk = function (comp) {
                comp.components().forEach(walk);
                if (!comp.get("editable")) {
                    return;
                }
                const view = comp.view;
                if (!view || !view.el) {
                    return;
                }
                const html = view.el.innerHTML;
                if (
                    !html ||
                    (html.indexOf("{{") < 0 && html.indexOf("gjs-mail-placeholder") < 0)
                ) {
                    return;
                }
                const wrapped = self.wrapMailPlaceholdersInHtml(html);
                if (wrapped !== html) {
                    comp.components(wrapped);
                }
            };
            walk(editor.getWrapper());
        },
        insertMailPlaceholderCaretMarker(rteInst) {
            this.pendingMailPlaceholderRte = rteInst;
            this.pendingCaretMarkerId = null;
            this.savedMailPlaceholderRange = null;
            if (!rteInst || !rteInst.el) {
                return false;
            }
            try {
                const el = rteInst.el;
                const doc = rteInst.doc || el.ownerDocument;
                const sel = typeof rteInst.selection === "function" ? rteInst.selection() : doc.getSelection();
                let r = null;
                if (sel && sel.rangeCount >= 1) {
                    const r0 = sel.getRangeAt(0);
                    if (el.contains(r0.commonAncestorContainer) || el === r0.commonAncestorContainer) {
                        r = r0;
                    }
                }
                if (!r && this.lastMailRteCaretRange) {
                    const lr = this.lastMailRteCaretRange;
                    if (
                        lr &&
                        (el.contains(lr.commonAncestorContainer) || el === lr.commonAncestorContainer)
                    ) {
                        r = lr.cloneRange();
                        sel.removeAllRanges();
                        sel.addRange(r);
                    }
                }
                if (!r) {
                    return false;
                }
                if (!r.collapsed) {
                    r.collapse(false);
                }
                const id = "gjs-ph-m-" + Date.now() + "-" + String(Math.floor(Math.random() * 1e9));
                const marker = doc.createElement("span");
                marker.id = id;
                marker.className = "gjs-mail-caret-marker";
                marker.setAttribute("contenteditable", "false");
                marker.setAttribute("data-gjs-ph-marker", "1");
                marker.appendChild(doc.createTextNode("\u200b"));
                r.insertNode(marker);
                const r2 = doc.createRange();
                r2.setStartAfter(marker);
                r2.collapse(true);
                sel.removeAllRanges();
                sel.addRange(r2);
                this.pendingCaretMarkerId = id;
                return true;
            } catch (e) {
                return false;
            }
        },
        captureMailPlaceholderSelection(rteInst) {
            this.pendingMailPlaceholderRte = rteInst;
            this.savedMailPlaceholderRange = null;
            if (!rteInst || !rteInst.el) {
                return;
            }
            try {
                const el = rteInst.el;
                const doc = rteInst.doc || el.ownerDocument;
                const sel = typeof rteInst.selection === "function" ? rteInst.selection() : doc.getSelection();
                if (!sel || sel.rangeCount < 1) {
                    return;
                }
                const r = sel.getRangeAt(0);
                if (el.contains(r.commonAncestorContainer) || el === r.commonAncestorContainer) {
                    this.savedMailPlaceholderRange = r.cloneRange();
                }
            } catch (e) {}
        },
        removeMailPlaceholderCaretMarker() {
            const rteInst = this.pendingMailPlaceholderRte;
            const id = this.pendingCaretMarkerId;
            if (!rteInst || !rteInst.el || !id) {
                return;
            }
            try {
                const doc = rteInst.doc || rteInst.el.ownerDocument;
                const m = doc.getElementById(id);
                if (m && m.parentNode) {
                    m.parentNode.removeChild(m);
                }
            } catch (e) {}
        },
        attachMailRteCaretTracking(editor) {
            const vm = this;
            const bind = function () {
                if (!editor.Canvas || typeof editor.Canvas.getFrameEl !== "function") {
                    return;
                }
                const frame = editor.Canvas.getFrameEl();
                if (!frame || !frame.contentDocument) {
                    return;
                }
                const doc = frame.contentDocument;
                if (doc._supsisMailRteCaretBound) {
                    return;
                }
                doc._supsisMailRteCaretBound = true;
                doc.addEventListener("selectionchange", function () {
                    const selected = editor.getSelected();
                    if (!selected || !selected.get("editable")) {
                        return;
                    }
                    const view = selected.view;
                    if (!view || !view.el) {
                        return;
                    }
                    const el = view.el;
                    try {
                        const s = doc.getSelection();
                        if (!s || s.rangeCount < 1) {
                            return;
                        }
                        const rg = s.getRangeAt(0);
                        if (!el.contains(rg.commonAncestorContainer) && el !== rg.commonAncestorContainer) {
                            return;
                        }
                        vm.lastMailRteCaretRange = rg.cloneRange();
                    } catch (e) {}
                });
            };
            editor.on("load", function () {
                setTimeout(bind, 0);
            });
            setTimeout(bind, 0);
        },
        restoreMailPlaceholderSelectionThenInsert(rteInst, html) {
            if (!rteInst || !rteInst.el) {
                return;
            }
            const el = rteInst.el;
            const doc = rteInst.doc || el.ownerDocument;
            const win = doc.defaultView;
            const vm = this;
            if (vm.editor && vm.editor.Canvas) {
                try {
                    const frame = vm.editor.Canvas.getFrameEl();
                    if (frame && frame.contentWindow) {
                        frame.contentWindow.focus();
                    }
                } catch (e) {}
            }
            el.focus();
            const toInsert = html + "\u200B";
            try {
                const sel = typeof rteInst.selection === "function" ? rteInst.selection() : win.getSelection();
                sel.removeAllRanges();
                const range = this.savedMailPlaceholderRange;
                let useRange = range;
                if (useRange) {
                    const ok =
                        el.contains(useRange.commonAncestorContainer) || el === useRange.commonAncestorContainer;
                    if (!ok) {
                        useRange = null;
                    }
                }
                if (useRange) {
                    sel.addRange(useRange);
                } else {
                    const r2 = doc.createRange();
                    r2.selectNodeContents(el);
                    r2.collapse(false);
                    sel.addRange(r2);
                }
            } catch (e) {
                try {
                    const sel = win.getSelection();
                    const r2 = doc.createRange();
                    r2.selectNodeContents(el);
                    r2.collapse(false);
                    sel.removeAllRanges();
                    sel.addRange(r2);
                } catch (e2) {}
            }
            this.savedMailPlaceholderRange = null;
            if (typeof rteInst.insertHTML === "function") {
                rteInst.insertHTML(toInsert);
            } else if (typeof rteInst.exec === "function") {
                rteInst.exec("insertHTML", toInsert);
            } else {
                doc.execCommand("insertHTML", false, toInsert);
            }
            try {
                const selAfter = typeof rteInst.selection === "function" ? rteInst.selection() : win.getSelection();
                if (selAfter && selAfter.rangeCount > 0) {
                    const ra = selAfter.getRangeAt(0);
                    ra.collapse(false);
                    selAfter.removeAllRanges();
                    selAfter.addRange(ra);
                }
            } catch (e3) {}
        },
        openMailPlaceholderModal() {
            this.mailPlaceholderInput = "";
            this.mailPlaceholderModalOpen = true;
            const self = this;
            this.$nextTick(function () {
                const ref = self.$refs.mailPlaceholderInput;
                if (!ref) {
                    return;
                }
                if (typeof ref.focus === "function") {
                    ref.focus();
                } else if (ref.$refs && ref.$refs.input) {
                    ref.$refs.input.focus();
                }
            });
        },
        confirmMailPlaceholderInsert() {
            const raw = this.mailPlaceholderInput;
            if (!raw || !String(raw).trim()) {
                this.$message.warning("Placeholder yolu girin");
                return;
            }
            const rteInst = this.pendingMailPlaceholderRte;
            const markerId = this.pendingCaretMarkerId;
            let clean = String(raw).trim().replace(/^\{\{|\}\}$/g, "");
            const toInsert = this.buildPlaceholderSpanHtml(clean);
            const htmlWithZwsp = toInsert + "\u200b";
            this.mailPlaceholderModalOpen = false;
            this.mailPlaceholderInput = "";
            const vm = this;
            setTimeout(function () {
                if (!rteInst || !rteInst.el) {
                    vm.pendingMailPlaceholderRte = null;
                    vm.pendingCaretMarkerId = null;
                    return;
                }
                const doc = rteInst.doc || rteInst.el.ownerDocument;
                if (vm.editor && vm.editor.Canvas) {
                    try {
                        const frame = vm.editor.Canvas.getFrameEl();
                        if (frame && frame.contentWindow) {
                            frame.contentWindow.focus();
                        }
                    } catch (e) {}
                }
                rteInst.el.focus();
                const marker = markerId ? doc.getElementById(markerId) : null;
                if (marker && marker.parentNode) {
                    marker.outerHTML = htmlWithZwsp;
                } else {
                    vm.restoreMailPlaceholderSelectionThenInsert(rteInst, toInsert);
                }
                vm.pendingMailPlaceholderRte = null;
                vm.pendingCaretMarkerId = null;
                vm.savedMailPlaceholderRange = null;
            }, 50);
        },
        cancelMailPlaceholderModal() {
            this.removeMailPlaceholderCaretMarker();
            this.mailPlaceholderModalOpen = false;
            this.pendingMailPlaceholderRte = null;
            this.mailPlaceholderInput = "";
            this.savedMailPlaceholderRange = null;
            this.pendingCaretMarkerId = null;
        },
        setupMailPlaceholders(editor) {
            const vm = this;
            const rte = editor.RichTextEditor;
            if (rte && typeof rte.add === "function") {
                rte.add("mail-placeholder", {
                    event: "mousedown",
                    icon:
                        '<span class="gjs-rte-mail-placeholder-wrap" style="display:flex;align-items:center" title="Şablon özelliği ekle" aria-label="Şablon özelliği ekle">' +
                        '<i class="material-icons gjs-rte-mail-placeholder-mi" style="font-size:15px">data_object</i>' +
                        "</span>",
                    attributes: {
                        title: "Şablon özelliği ekle",
                        "aria-label": "Şablon özelliği ekle",
                    },
                    result: function (rteInst) {
                        const placed = vm.insertMailPlaceholderCaretMarker(rteInst);
                        if (!placed) {
                            vm.captureMailPlaceholderSelection(rteInst);
                        }
                        vm.openMailPlaceholderModal();
                    },
                });
                editor.on("load", function () {
                    setTimeout(function () {
                        try {
                            var act =
                                typeof rte.get === "function" ? rte.get("mail-placeholder") : null;
                            if (act && act.btn) {
                                act.btn.addEventListener(
                                    "mousedown",
                                    function (e) {
                                        e.preventDefault();
                                    },
                                    true
                                );
                            }
                        } catch (e) {}
                    }, 0);
                });
            }
            vm.attachMailRteCaretTracking(editor);
            editor.on("rte:disable", function () {
                const selected = editor.getSelected();
                if (!selected || !selected.get("editable")) {
                    return;
                }
                const view = selected.view;
                if (!view || !view.el) {
                    return;
                }
                const html = view.el.innerHTML;
                if (
                    !html ||
                    (html.indexOf("{{") < 0 && html.indexOf("gjs-mail-placeholder") < 0)
                ) {
                    return;
                }
                const wrapped = vm.wrapMailPlaceholdersInHtml(html);
                if (wrapped !== html) {
                    selected.components(wrapped);
                }
            });
            editor.on("load", function () {
                setTimeout(function () {
                    vm.wrapAllMailPlaceholders(editor);
                }, 450);
            });
        },
        getRawHtml() {
            try {
                if (!this.editor) {
                    return "";
                }
                let html = this.editor.getHtml();
                html = html.replace(
                    /<span[^>]*class="[^"]*gjs-mail-caret-marker[^"]*"[^>]*>[\s\S]*?<\/span>/gi,
                    ""
                );
                html = this.stripMailPlaceholderSpans(html);
                html = html.replace(/\u200B/g, "");
                const out = mjml2html(html);
                return out && out.html ? out.html : "";
            } catch (error) {
                console.error(error);
                return "";
            }
        },
        handleUpdateTemplate() {
            const updatedHtml = this.getRawHtml();
            const updatedDesign = this.editor.getComponents();

            const updatingFields = {
                html: updatedHtml,
                design: updatedDesign,
            };

            MAIL_API.TEMPLATE.updateEmailTemplate(this.template._id, updatingFields)
                .then((updatedTemplate) => {
                    this.$message.success(this.$t("mail.templateUpdateSucs"));
                    this.$emit("template-updated", updatedTemplate);
                })
                .catch((error) => {
                    console.error("Şablon güncellenirken bir hata oluştu:", error);
                    this.$message.error(this.$t("mail.templateUpdateError"));
                });
        },
    },
});
</script>

<style lang="scss">
.email-template-editor {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 10px;

    .grape-wrapper {
        height: calc(100vh - 156px) !important;
        max-height: calc(100vh - 156px) !important;

        &.preview-mode {
            height: 100vh !important;
            max-height: 100vh !important;
        }
    }

    .toolbar {
        margin-bottom: 10px;
        display: flex;
        justify-content: space-between;
        gap: 10px;

        .left-buttons,
        .right-buttons {
            display: flex;
            gap: 10px;
        }
    }

    iframe {
        height: 100% !important;
    }

    &:deep(.gjs-rte-mail-placeholder-wrap) {
        display: flex;
        align-items: center;
        vertical-align: middle;
        line-height: 1;
    }

    &:deep(i.gjs-rte-mail-placeholder-mi.material-icons),
    &:deep(i.gjs-rte-mail-placeholder-mi) {
        font-size: 15px !important;
        line-height: 1 !important;
        color: #616161;
        opacity: 0.95;
        font-style: normal;
    }

    &:deep(.gjs-cv-canvas) {
        width: 100%;
        height: 100%;
        top: 0;
    }

    &:deep(.gjs-pn-panel) {
        padding: 0;
    }

    .gjs-preview-mode {

        .gjs-pn-panels,
        .gjs-dev-name,
        .gjs-frame-wrapper--no-select,
        .gjs-badges,
        .gjs-toolbar,
        .gjs-tools,
        .gjs-resizer,
        .gjs-highlighter,
        .gjs-offset-viewer,
        .gjs-selected {
            display: none !important;
        }

        .gjs-cv-canvas {
            width: 100% !important;
            height: 100% !important;
            left: 0 !important;
            top: 0 !important;
            padding: 0 !important;
        }

        .gjs-frame {
            border: none !important;
        }

        .gjs-cv-canvas-bg {
            width: 100% !important;
            height: 100% !important;
            top: 0 !important;
        }
    }

    // GrapesJS Asset Manager Özelleştirmeleri - Default UI'ları gizle
    &:deep(.gjs-am-file-uploader) {
        display: none !important;
    }

    &:deep(.gjs-am-add-asset) {
        display: none !important;
    }

    .gjs-asset-manager {
        display: flex !important;
        width: 100% !important;
    }

    // Asset Manager Modal Customize
    &:deep(.gjs-mdl-dialog) {
        background: #22272d !important;
        box-shadow: 0 0 30px rgba(0, 0, 0, 0.5) !important;

        .gjs-mdl-title {
            background: #1a1f24 !important;
            color: #fff !important;
            border-bottom: 1px solid #2d3339 !important;
            font-weight: 600 !important;
        }

        .gjs-mdl-content {
            background: #22272d !important;
            color: #e0e0e0 !important;
        }

        .gjs-am-assets-header {
            display: none !important; // URL ile manuel ekleme alanını gizle
        }

        .gjs-am-assets-cont {
            background: #22272d !important;
            padding-top: 0 !important;
        }

        .gjs-am-assets {
            background: #22272d !important;
            padding: 10px !important;
        }

        .gjs-am-asset {
            background: #2d3339 !important;
            border: 2px solid #3a4149 !important;
            border-radius: 8px !important;
            transition: all 0.3s ease !important;

            &:hover {
                border-color: #4a5560 !important;
                transform: translateY(-2px) !important;
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3) !important;
            }

            .gjs-am-preview {
                background: #1a1f24 !important;
            }

            .gjs-am-meta {
                background: #2d3339 !important;
                color: #e0e0e0 !important;
                border-top: 1px solid #3a4149 !important;
            }

            .gjs-am-name {
                color: #e0e0e0 !important;
            }

            .gjs-am-close {
                background: #e74c3c !important;
                color: #fff !important;
                border-radius: 50% !important;
                width: 24px !important;
                height: 24px !important;
                display: flex !important;
                align-items: center !important;
                justify-content: center !important;

                &:hover {
                    background: #c0392b !important;
                }
            }
        }
    }

    // Modal backdrop
    &:deep(.gjs-mdl-dialog-bg) {
        background: rgba(34, 39, 45, 0.85) !important;
    }
}

// Custom Asset Upload UI - Global styles
.custom-asset-upload {
    padding: 15px;
    margin: 0;
    width: 60%;
    border-bottom: 2px solid #2d3339;

    .asset-upload-area {
        border: 2px dashed #4a5560;
        border-radius: 8px;
        padding: 35px 25px;
        text-align: center;
        background: linear-gradient(135deg, #2d3339 0%, #22272d 100%);
        transition: all 0.3s ease;
        cursor: pointer;
        position: relative;
        overflow: hidden;

        &::before {
            content: '';
            position: absolute;
            top: 0;
            left: -100%;
            width: 100%;
            height: 100%;
            background: linear-gradient(90deg, transparent, rgba(74, 85, 96, 0.2), transparent);
            transition: left 0.5s;
        }

        &:hover {
            border-color: #5a6570;
            background: linear-gradient(135deg, #353b42 0%, #2a3038 100%);
            box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
            transform: translateY(-2px);

            &::before {
                left: 100%;
            }

            i {
                transform: scale(1.1);
            }
        }

        &.dragover {
            border-color: #6a7580;
            border-style: solid;
            background: linear-gradient(135deg, #3a4149 0%, #2f353d 100%);
            box-shadow: 0 0 20px rgba(106, 117, 128, 0.4);

            i {
                animation: bounce 0.6s ease infinite;
            }
        }

        .asset-upload-label {
            cursor: pointer;
            display: block;
            margin: 0;
            position: relative;
            z-index: 1;

            i {
                font-size: 42px;
                color: #8a9299;
                display: block;
                margin-bottom: 14px;
                transition: all 0.3s ease;
            }

            p {
                font-size: 16px;
                font-weight: 600;
                color: #e0e0e0;
                margin: 0 0 8px 0;
                letter-spacing: 0.3px;
            }

            span {
                font-size: 13px;
                color: #8a9299;
                display: block;
                font-weight: 500;
            }
        }
    }
}

@keyframes bounce {

    0%,
    100% {
        transform: translateY(0);
    }

    50% {
        transform: translateY(-10px);
    }
}
</style>

<style lang="scss">
/* GrapesJS RTE: scoped + .material-icons (24px) yüzünden i’ye doğrudan global hedef */
.gjs-rte-toolbar i.gjs-rte-mail-placeholder-mi.material-icons,
.gjs-rte-toolbar i.material-icons.gjs-rte-mail-placeholder-mi,
.gjs-editor i.gjs-rte-mail-placeholder-mi.material-icons {
    font-size: 15px !important;
    line-height: 1 !important;
    color: #616161;
    opacity: 0.95;
}

/* a-modal içeriği body'ye taşınabildiği için scoped dışında */
.mail-placeholder-modal-actions {
    margin-top: 16px;
    display: flex;
    justify-content: flex-end;
    gap: 8px;
}
</style>
